import { ChangeDetectionStrategy, Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CupomService, SalvarCupomPayload, CupomEstatisticas, CupomAplicacaoRecente } from '../../../core/cupom/cupom.service';
import { Cupom } from '../../../core/cupom/cupom.model';
import { RegraProcedimentoService } from '../../../core/regra-procedimento/regra-procedimento.service';
import { RegraProcedimento, SalvarRegraPayload } from '../../../core/regra-procedimento/regra-procedimento.model';
import { CatalogoService } from '../../../core/catalogo/catalogo.service';
import { ClassificacaoItem, ProcedimentoItem, PlanoB2cItem, EmpresaItem, EstabelecimentoItem } from '../../../core/catalogo/catalogo.model';
import { MatrizValorService } from '../../../core/matriz-valor/matriz-valor.service';
import { MatrizValor, SalvarMatrizPayload } from '../../../core/matriz-valor/matriz-valor.model';
import { RegraPlanoService } from '../../../core/regra-plano/regra-plano.service';
import { RegraPlano } from '../../../core/regra-plano/regra-plano.model';
import { RegraEmpresaService } from '../../../core/regra-empresa/regra-empresa.service';
import { RegraEmpresa } from '../../../core/regra-empresa/regra-empresa.model';
import { RegraEstabelecimentoService } from '../../../core/regra-estabelecimento/regra-estabelecimento.service';
import { RegraEstabelecimento } from '../../../core/regra-estabelecimento/regra-estabelecimento.model';
import { CupomComboService } from '../../../core/cupom-combo/cupom-combo.service';
import { CupomCombo } from '../../../core/cupom-combo/cupom-combo.model';
import { RegraDependenciaService } from '../../../core/regra-dependencia/regra-dependencia.service';
import { RegraDependencia } from '../../../core/regra-dependencia/regra-dependencia.model';

const TIPO_LABELS: Record<string, string> = { P: 'Todos', C: 'B2C', E: 'Empresa', A: 'Assinantes', G: 'Geral' };
const DESC_LABELS: Record<string, string> = { P: 'Percentual', F: 'Preco fixo', R: 'Real fixo', V: 'Voucher' };

@Component({
  selector: 'app-cupom-edit',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './cupom-edit.component.html',
  styleUrl: './cupom-edit.component.scss',
})
export class CupomEditComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cupomSvc = inject(CupomService);
  private readonly regraSvc = inject(RegraProcedimentoService);
  private readonly catalogoSvc = inject(CatalogoService);
  private readonly matrizSvc = inject(MatrizValorService);
  private readonly regraPlanoSvc = inject(RegraPlanoService);
  private readonly regraEmpresaSvc = inject(RegraEmpresaService);
  private readonly regraEstabSvc = inject(RegraEstabelecimentoService);
  private readonly comboSvc = inject(CupomComboService);
  private readonly depSvc = inject(RegraDependenciaService);

  protected readonly cupom = signal<Cupom | null>(null);
  protected readonly loading = signal(true);
  protected readonly salvando = signal(false);
  protected readonly erro = signal<string | null>(null);
  protected readonly sucesso = signal<string | null>(null);
  protected readonly editando = signal(false);
  protected readonly stats = signal<CupomEstatisticas | null>(null);
  protected readonly aplicacoes = signal<CupomAplicacaoRecente[]>([]);
  protected readonly tab = signal<'cadastro' | 'uso'>('cadastro');

  protected editForm: {
    codCupom: string; dsCupom: string; dsObservacao: string;
    ieTipo: string; ieTipoDesconto: string;
    prDesconto: number | null; vlDesconto: number | null; vlTeto: number | null;
    dtVigenciaIni: string; dtVigenciaFim: string;
    qtUsoMaxGlobal: number | null; qtUsoMaxPessoa: number | null; nrDiasBloqueioReuso: number | null;
    ieBeneficiario: string;
    ieEmissaoManual: boolean;
  } = { codCupom: '', dsCupom: '', dsObservacao: '', ieTipo: 'P', ieTipoDesconto: 'P', prDesconto: null, vlDesconto: null, vlTeto: null, dtVigenciaIni: '', dtVigenciaFim: '', qtUsoMaxGlobal: null, qtUsoMaxPessoa: null, nrDiasBloqueioReuso: null, ieBeneficiario: 'T', ieEmissaoManual: false };

  protected readonly regras = signal<RegraProcedimento[]>([]);
  protected readonly classificacoes = signal<ClassificacaoItem[]>([]);
  protected readonly classifSelecionada = signal<number | null>(null);
  protected readonly buscaProc = signal('');
  protected readonly resultadosBusca = signal<ProcedimentoItem[]>([]);
  protected readonly buscando = signal(false);
  protected readonly classifBusca = signal('');
  protected readonly classifFiltradas = computed(() => {
    const q = this.classifBusca().trim().toLowerCase();
    const base = this.classificacoes();
    const lista = q ? base.filter(c => c.dsClassificacao.toLowerCase().includes(q)) : base;
    return lista.slice(0, 50);
  });
  private procTimer: ReturnType<typeof setTimeout> | null = null;

  protected readonly faixas = signal<MatrizValor[]>([]);
  protected novaFaixaDesc = '';
  protected novaFaixaPr: number | null = null;
  protected novaFaixaVl: number | null = null;
  protected novaFaixaTeto: number | null = null;

  protected readonly regrasPlano = signal<RegraPlano[]>([]);
  protected readonly planosB2c = signal<PlanoB2cItem[]>([]);
  protected readonly planoSelecionado = signal<number | null>(null);

  protected readonly regrasEmpresa = signal<RegraEmpresa[]>([]);
  protected readonly empresas = signal<EmpresaItem[]>([]);
  protected readonly empresaSelecionada = signal<number | null>(null);

  protected readonly regrasEstab = signal<RegraEstabelecimento[]>([]);
  protected readonly estabelecimentos = signal<EstabelecimentoItem[]>([]);
  protected readonly estabSelecionado = signal<number | null>(null);

  protected readonly combos = signal<CupomCombo[]>([]);
  protected readonly buscaCupomCombo = signal('');
  protected readonly resultadosBuscaCupom = signal<import('../../../core/cupom/cupom.model').CupomListado[]>([]);

  protected readonly dependencias = signal<RegraDependencia[]>([]);
  protected readonly buscaCupomDep = signal('');
  protected readonly resultadosBuscaCupomDep = signal<import('../../../core/cupom/cupom.model').CupomListado[]>([]);

  private nrSeqCupom = 0;

  ngOnInit(): void {
    this.nrSeqCupom = Number(this.route.snapshot.paramMap.get('id'));
    if (!this.nrSeqCupom) { this.loading.set(false); return; }

    this.cupomSvc.obter(this.nrSeqCupom).subscribe({
      next: res => {
        this.cupom.set(res.data ?? null);
        this.loading.set(false);
      },
      error: () => { this.erro.set('Falha ao carregar cupom.'); this.loading.set(false); },
    });

    this.regraSvc.listar(this.nrSeqCupom).subscribe({ next: res => this.regras.set(res.data ?? []) });
    this.catalogoSvc.listarClassificacoes().subscribe({ next: res => { if (res.data) this.classificacoes.set(res.data); } });
    this.matrizSvc.listar(this.nrSeqCupom).subscribe({ next: res => { if (res.data) this.faixas.set(res.data); } });
    this.regraPlanoSvc.listar(this.nrSeqCupom).subscribe({ next: res => this.regrasPlano.set(res.data ?? []) });
    this.regraEmpresaSvc.listar(this.nrSeqCupom).subscribe({ next: res => this.regrasEmpresa.set(res.data ?? []) });
    this.regraEstabSvc.listar(this.nrSeqCupom).subscribe({ next: res => this.regrasEstab.set(res.data ?? []) });
    this.comboSvc.listar(this.nrSeqCupom).subscribe({ next: res => this.combos.set(res.data ?? []) });
    this.depSvc.listar(this.nrSeqCupom).subscribe({ next: res => this.dependencias.set(res.data ?? []) });
    this.catalogoSvc.listarPlanosB2c().subscribe({ next: res => { if (res.data) this.planosB2c.set(res.data); } });
    this.catalogoSvc.listarEmpresas().subscribe({ next: res => { if (res.data) this.empresas.set(res.data); } });
    this.catalogoSvc.listarEstabelecimentos().subscribe({ next: res => { if (res.data) this.estabelecimentos.set(res.data); } });
    this.cupomSvc.estatisticas(this.nrSeqCupom).subscribe({ next: res => { if (res.data) this.stats.set(res.data); } });
    this.cupomSvc.ultimasAplicacoes(this.nrSeqCupom).subscribe({ next: res => { if (res.data) this.aplicacoes.set(res.data); } });
  }

  tipoLabel(): string { return TIPO_LABELS[this.cupom()?.ieTipo ?? ''] ?? this.cupom()?.ieTipo ?? ''; }

  descontoLabel(): string {
    const c = this.cupom();
    if (!c) return '';
    if (c.ieTipoDesconto === 'P' && c.prDesconto != null) return `${c.prDesconto}%`;
    if (c.ieTipoDesconto === 'V') return '100% (voucher)';
    if (c.vlDesconto != null) return `R$ ${c.vlDesconto}`;
    return DESC_LABELS[c.ieTipoDesconto] ?? c.ieTipoDesconto;
  }

  vigenciaLabel(): string {
    const c = this.cupom();
    if (!c) return '';
    const ini = c.dtVigenciaIni ? this.fmt(c.dtVigenciaIni) : '--';
    const fim = c.dtVigenciaFim ? this.fmt(c.dtVigenciaFim) : 'sem prazo';
    return `${ini} a ${fim}`;
  }

  situacaoLabel(): string {
    const s = this.cupom()?.ieSituacao;
    if (s === 'A') return 'Ativo';
    if (s === 'I') return 'Inativo';
    if (s === 'S') return 'Sazonal';
    return s ?? '';
  }

  limitesLabel(): string {
    const c = this.cupom();
    if (!c) return '';
    const por = c.qtUsoMaxPessoa ?? 1;
    const total = c.qtUsoMaxGlobal ? `teto global ${c.qtUsoMaxGlobal}` : 'sem teto global';
    return `${por} uso/pessoa, ${total}`;
  }

  iniciarEdicao(): void {
    const c = this.cupom();
    if (!c) return;
    this.editForm = {
      codCupom: c.codCupom, dsCupom: c.dsCupom, dsObservacao: c.dsObservacao ?? '',
      ieTipo: c.ieTipo, ieTipoDesconto: c.ieTipoDesconto,
      prDesconto: c.prDesconto ?? null, vlDesconto: c.vlDesconto ?? null, vlTeto: c.vlTeto ?? null,
      dtVigenciaIni: c.dtVigenciaIni ? c.dtVigenciaIni.substring(0, 10) : '',
      dtVigenciaFim: c.dtVigenciaFim ? c.dtVigenciaFim.substring(0, 10) : '',
      qtUsoMaxGlobal: c.qtUsoMaxGlobal ?? null, qtUsoMaxPessoa: c.qtUsoMaxPessoa ?? null,
      nrDiasBloqueioReuso: c.nrDiasBloqueioReuso ?? null,
      ieBeneficiario: c.ieBeneficiario ?? 'T',
      ieEmissaoManual: c.ieEmissaoManual === 'S',
    };
    this.editando.set(true);
  }

  cancelarEdicao(): void { this.editando.set(false); }

  salvarEdicao(): void {
    const c = this.cupom();
    if (!c) return;
    const f = this.editForm;
    const payload: SalvarCupomPayload = {
      codCupom: f.codCupom, dsCupom: f.dsCupom, dsObservacao: f.dsObservacao || null,
      ieTipo: f.ieTipo, ieTipoDesconto: f.ieTipoDesconto,
      prDesconto: f.prDesconto, vlDesconto: f.vlDesconto, vlTeto: f.vlTeto,
      dtVigenciaIni: f.dtVigenciaIni, dtVigenciaFim: f.dtVigenciaFim || null,
      qtUsoMaxGlobal: f.qtUsoMaxGlobal, qtUsoMaxPessoa: f.qtUsoMaxPessoa,
      nrDiasBloqueioReuso: f.nrDiasBloqueioReuso,
      ieBeneficiario: f.ieBeneficiario || 'T',
      iePaiCombo: c.iePaiCombo, ieEmissaoManual: f.ieEmissaoManual ? 'S' : 'N', iePermiteSupForaVigencia: 'N', ieSituacao: c.ieSituacao,
    };
    this.salvando.set(true); this.limparMsg();
    this.cupomSvc.atualizar(this.nrSeqCupom, payload).subscribe({
      next: () => {
        this.salvando.set(false); this.editando.set(false); this.sucesso.set('Cupom atualizado.');
        this.cupomSvc.obter(this.nrSeqCupom).subscribe({ next: res => this.cupom.set(res.data ?? null) });
      },
      error: e => { this.salvando.set(false); this.erro.set(e?.error?.error?.message ?? 'Falha ao salvar.'); },
    });
  }

  nomeClassif(nrSeqClassif: number): string {
    return this.classificacoes().find(x => x.nrSeqClassif === nrSeqClassif)?.dsClassificacao ?? `Classif. ${nrSeqClassif}`;
  }

  excluirRegra(nrSequencia: number): void {
    this.salvando.set(true); this.limparMsg();
    this.regraSvc.excluir(nrSequencia).subscribe({
      next: () => { this.regras.update(arr => arr.filter(r => r.nrSequencia !== nrSequencia)); this.salvando.set(false); this.sucesso.set('Regra removida.'); },
      error: () => { this.salvando.set(false); this.erro.set('Falha ao remover regra.'); },
    });
  }

  adicionarClassificacao(): void {
    const sel = this.classifSelecionada();
    if (sel == null || !this.cupom()) return;
    this.criarRegra({ nrSeqClassif: sel, ieModo: 'I' });
    this.classifSelecionada.set(null);
  }

  // Autocomplete da classificacao: adiciona direto a clicada e limpa a busca.
  selecionarClassif(cat: ClassificacaoItem): void {
    this.classifSelecionada.set(cat.nrSeqClassif);
    this.adicionarClassificacao();
    this.classifBusca.set('');
  }

  // Autocomplete do procedimento: busca server-side com debounce conforme digita.
  onBuscaProcChange(termo: string): void {
    this.buscaProc.set(termo);
    if (this.procTimer) clearTimeout(this.procTimer);
    if (termo.trim().length < 2) { this.resultadosBusca.set([]); return; }
    this.procTimer = setTimeout(() => this.buscarProcedimentos(), 300);
  }

  buscarProcedimentos(): void {
    const termo = this.buscaProc().trim();
    if (termo.length < 2) { this.resultadosBusca.set([]); return; }
    this.buscando.set(true);
    this.catalogoSvc.buscarProcedimentos(termo, undefined, 20).subscribe({
      next: res => { this.buscando.set(false); this.resultadosBusca.set(res.data ?? []); },
      error: () => this.buscando.set(false),
    });
  }

  adicionarProcedimento(proc: ProcedimentoItem): void {
    if (!this.cupom()) return;
    this.criarRegra({ cdProcedimento: proc.cdProcedimento, ieModo: 'I' });
    this.resultadosBusca.set([]); this.buscaProc.set('');
  }

  private criarRegra(payload: SalvarRegraPayload): void {
    this.salvando.set(true); this.limparMsg();
    this.regraSvc.criar(this.nrSeqCupom, payload).subscribe({
      next: () => {
        this.salvando.set(false); this.sucesso.set('Regra adicionada.');
        this.regraSvc.listar(this.nrSeqCupom).subscribe({ next: res => this.regras.set(res.data ?? []) });
      },
      error: e => { this.salvando.set(false); this.erro.set(e?.error?.error?.message ?? 'Falha ao adicionar regra.'); },
    });
  }

  adicionarFaixa(): void {
    if (!this.cupom()) return;
    const payload: SalvarMatrizPayload = {
      nrOrdem: this.faixas().length + 1, dsRegra: this.novaFaixaDesc || null,
      ieExigeCartaoSim: 'N', prDesconto: this.novaFaixaPr, vlFixo: this.novaFaixaVl, vlTeto: this.novaFaixaTeto,
    };
    this.salvando.set(true); this.limparMsg();
    this.matrizSvc.criar(this.nrSeqCupom, payload).subscribe({
      next: () => {
        this.salvando.set(false); this.sucesso.set('Faixa adicionada.');
        this.novaFaixaDesc = ''; this.novaFaixaPr = null; this.novaFaixaVl = null; this.novaFaixaTeto = null;
        this.matrizSvc.listar(this.nrSeqCupom).subscribe({ next: res => { if (res.data) this.faixas.set(res.data); } });
      },
      error: e => { this.salvando.set(false); this.erro.set(e?.error?.error?.message ?? 'Falha ao adicionar faixa.'); },
    });
  }

  excluirFaixa(nrSequencia: number): void {
    this.salvando.set(true); this.limparMsg();
    this.matrizSvc.excluir(nrSequencia).subscribe({
      next: () => { this.faixas.update(arr => arr.filter(f => f.nrSequencia !== nrSequencia)); this.salvando.set(false); this.sucesso.set('Faixa removida.'); },
      error: () => { this.salvando.set(false); this.erro.set('Falha ao remover faixa.'); },
    });
  }

  nomePlano(cdPlan: number): string { return this.planosB2c().find(p => p.cdPlano === cdPlan)?.dsPlano ?? `Plano ${cdPlan}`; }

  adicionarRegraPlano(): void {
    const sel = this.planoSelecionado();
    if (sel == null) return;
    this.salvando.set(true); this.limparMsg();
    this.regraPlanoSvc.criar(this.nrSeqCupom, { cdPlan: sel, ieModo: 'I' }).subscribe({
      next: () => {
        this.salvando.set(false); this.sucesso.set('Plano adicionado.'); this.planoSelecionado.set(null);
        this.regraPlanoSvc.listar(this.nrSeqCupom).subscribe({ next: res => this.regrasPlano.set(res.data ?? []) });
      },
      error: e => { this.salvando.set(false); this.erro.set(e?.error?.error?.message ?? 'Falha ao adicionar plano.'); },
    });
  }

  excluirRegraPlano(nrSequencia: number): void {
    this.salvando.set(true); this.limparMsg();
    this.regraPlanoSvc.excluir(nrSequencia).subscribe({
      next: () => { this.regrasPlano.update(arr => arr.filter(r => r.nrSequencia !== nrSequencia)); this.salvando.set(false); this.sucesso.set('Plano removido.'); },
      error: () => { this.salvando.set(false); this.erro.set('Falha ao remover plano.'); },
    });
  }

  nomeEmpresa(cdEmpresa: number): string { return this.empresas().find(e => e.cdEmpresa === cdEmpresa)?.dsEmpresa ?? `Empresa ${cdEmpresa}`; }

  adicionarRegraEmpresa(): void {
    const sel = this.empresaSelecionada();
    if (sel == null) return;
    this.salvando.set(true); this.limparMsg();
    this.regraEmpresaSvc.criar(this.nrSeqCupom, { cdEmpresa: sel, ieModo: 'I' }).subscribe({
      next: () => {
        this.salvando.set(false); this.sucesso.set('Empresa adicionada.'); this.empresaSelecionada.set(null);
        this.regraEmpresaSvc.listar(this.nrSeqCupom).subscribe({ next: res => this.regrasEmpresa.set(res.data ?? []) });
      },
      error: e => { this.salvando.set(false); this.erro.set(e?.error?.error?.message ?? 'Falha ao adicionar empresa.'); },
    });
  }

  excluirRegraEmpresa(nrSequencia: number): void {
    this.salvando.set(true); this.limparMsg();
    this.regraEmpresaSvc.excluir(nrSequencia).subscribe({
      next: () => { this.regrasEmpresa.update(arr => arr.filter(r => r.nrSequencia !== nrSequencia)); this.salvando.set(false); this.sucesso.set('Empresa removida.'); },
      error: () => { this.salvando.set(false); this.erro.set('Falha ao remover empresa.'); },
    });
  }

  nomeEstab(cdEstabelecimento: number): string { return this.estabelecimentos().find(e => e.cdEstabelecimento === cdEstabelecimento)?.dsEstabelecimento ?? `Estab. ${cdEstabelecimento}`; }

  adicionarRegraEstab(): void {
    const sel = this.estabSelecionado();
    if (sel == null) return;
    this.salvando.set(true); this.limparMsg();
    this.regraEstabSvc.criar(this.nrSeqCupom, { cdEstabelecimento: sel, ieModo: 'I' }).subscribe({
      next: () => {
        this.salvando.set(false); this.sucesso.set('Estabelecimento adicionado.'); this.estabSelecionado.set(null);
        this.regraEstabSvc.listar(this.nrSeqCupom).subscribe({ next: res => this.regrasEstab.set(res.data ?? []) });
      },
      error: e => { this.salvando.set(false); this.erro.set(e?.error?.error?.message ?? 'Falha ao adicionar estabelecimento.'); },
    });
  }

  excluirRegraEstab(nrSequencia: number): void {
    this.salvando.set(true); this.limparMsg();
    this.regraEstabSvc.excluir(nrSequencia).subscribe({
      next: () => { this.regrasEstab.update(arr => arr.filter(r => r.nrSequencia !== nrSequencia)); this.salvando.set(false); this.sucesso.set('Estabelecimento removido.'); },
      error: () => { this.salvando.set(false); this.erro.set('Falha ao remover estabelecimento.'); },
    });
  }

  buscarCuponsCombo(): void {
    const termo = this.buscaCupomCombo().trim();
    if (termo.length < 2) { this.resultadosBuscaCupom.set([]); return; }
    this.cupomSvc.listar({ search: termo, take: 10 }).subscribe({ next: res => this.resultadosBuscaCupom.set(res.data?.items ?? []) });
  }

  adicionarCombo(cupom: import('../../../core/cupom/cupom.model').CupomListado): void {
    this.salvando.set(true); this.limparMsg();
    this.comboSvc.criar(this.nrSeqCupom, { nrSeqCupomFilho: cupom.nrSequencia, nrOrdem: this.combos().length + 1, nrDiasOffsetLiberacao: 0 }).subscribe({
      next: () => {
        this.salvando.set(false); this.sucesso.set('Combo adicionado.');
        this.buscaCupomCombo.set(''); this.resultadosBuscaCupom.set([]);
        this.comboSvc.listar(this.nrSeqCupom).subscribe({ next: res => this.combos.set(res.data ?? []) });
      },
      error: e => { this.salvando.set(false); this.erro.set(e?.error?.error?.message ?? 'Falha ao adicionar combo.'); },
    });
  }

  excluirCombo(nrSequencia: number): void {
    this.salvando.set(true); this.limparMsg();
    this.comboSvc.excluir(nrSequencia).subscribe({
      next: () => { this.combos.update(arr => arr.filter(c => c.nrSequencia !== nrSequencia)); this.salvando.set(false); this.sucesso.set('Combo removido.'); },
      error: () => { this.salvando.set(false); this.erro.set('Falha ao remover combo.'); },
    });
  }

  // Offset de liberacao (dias) do cupom-filho — editavel inline; salva via PUT /Cupom/combo/{id}.
  atualizarOffsetCombo(c: CupomCombo, valor: unknown): void {
    const n = Math.max(0, Math.floor(Number(valor) || 0));
    if (n === c.nrDiasOffsetLiberacao) return;
    this.salvando.set(true); this.limparMsg();
    this.comboSvc.atualizar(c.nrSequencia, {
      nrSeqCupomFilho: c.nrSeqCupomFilho,
      nrOrdem: c.nrOrdem,
      nrDiasOffsetLiberacao: n,
      nrDiasValidade: c.nrDiasValidade,
      ieRecorrente: c.ieRecorrente,
      qtRepeticoes: c.qtRepeticoes,
    }).subscribe({
      next: () => {
        this.salvando.set(false); this.sucesso.set('Offset atualizado.');
        this.comboSvc.listar(this.nrSeqCupom).subscribe({ next: res => this.combos.set(res.data ?? []) });
      },
      error: e => { this.salvando.set(false); this.erro.set(e?.error?.error?.message ?? 'Falha ao atualizar offset.'); },
    });
  }

  buscarCuponsDep(): void {
    const termo = this.buscaCupomDep().trim();
    if (termo.length < 2) { this.resultadosBuscaCupomDep.set([]); return; }
    this.cupomSvc.listar({ search: termo, take: 10 }).subscribe({ next: res => this.resultadosBuscaCupomDep.set(res.data?.items ?? []) });
  }

  adicionarDependencia(cupom: import('../../../core/cupom/cupom.model').CupomListado): void {
    this.salvando.set(true); this.limparMsg();
    this.depSvc.criar(this.nrSeqCupom, { nrSeqCupomDep: cupom.nrSequencia, ieTipoDep: 'U' }).subscribe({
      next: () => {
        this.salvando.set(false); this.sucesso.set('Dependencia adicionada.');
        this.buscaCupomDep.set(''); this.resultadosBuscaCupomDep.set([]);
        this.depSvc.listar(this.nrSeqCupom).subscribe({ next: res => this.dependencias.set(res.data ?? []) });
      },
      error: e => { this.salvando.set(false); this.erro.set(e?.error?.error?.message ?? 'Falha ao adicionar dependencia.'); },
    });
  }

  excluirDependencia(nrSequencia: number): void {
    this.salvando.set(true); this.limparMsg();
    this.depSvc.excluir(nrSequencia).subscribe({
      next: () => { this.dependencias.update(arr => arr.filter(d => d.nrSequencia !== nrSequencia)); this.salvando.set(false); this.sucesso.set('Dependencia removida.'); },
      error: () => { this.salvando.set(false); this.erro.set('Falha ao remover dependencia.'); },
    });
  }

  inativar(): void {
    if (!this.cupom() || !confirm('Inativar este cupom?')) return;
    this.salvando.set(true); this.limparMsg();
    this.cupomSvc.inativar(this.nrSeqCupom).subscribe({
      next: () => {
        this.salvando.set(false);
        this.cupom.update(c => c ? { ...c, ieSituacao: 'I' } : c);
        this.sucesso.set('Cupom inativado.');
      },
      error: e => { this.salvando.set(false); this.erro.set(e?.error?.error?.message ?? 'Falha ao inativar.'); },
    });
  }

  vigenciaRegraLabel(): string {
    const c = this.cupom();
    if (!c) return '';
    const ini = c.dtVigenciaIni ? `Iniciou ${this.fmt(c.dtVigenciaIni)}` : 'Sem inicio definido';
    const fim = c.dtVigenciaFim ? `ate ${this.fmt(c.dtVigenciaFim)}` : 'sem prazo final';
    return `${ini}, ${fim}`;
  }

  tipoRegraLabel(): string {
    const c = this.cupom();
    if (!c) return '';
    const labels: Record<string, string> = { P: 'Todos (qualquer cliente)', C: 'B2C (assinantes)', E: 'Empresa (B2B)', A: 'Assinantes', G: 'Geral (todos)' };
    return labels[c.ieTipo] ?? c.ieTipo;
  }

  procedimentosRegraLabel(): string {
    const r = this.regras();
    if (r.length === 0) return 'Todos os procedimentos (sem restricao)';
    const classifs = r.filter(x => x.nrSeqClassif != null);
    const procs = r.filter(x => x.cdProcedimento != null);
    const parts: string[] = [];
    if (classifs.length > 0) parts.push(`${classifs.length} classificacao(oes)`);
    if (procs.length > 0) parts.push(`${procs.length} procedimento(s)`);
    return parts.join(' + ');
  }

  limiteRegraLabel(): string {
    const c = this.cupom();
    if (!c) return '';
    const uso = c.qtUsoMaxPessoa ?? 1;
    const bloq = c.nrDiasBloqueioReuso;
    return `${uso} uso por paciente${bloq ? `, bloqueio de ${bloq} dias entre usos` : ', sem bloqueio entre usos'}`;
  }

  filtrosRegraLabel(): string {
    const parts: string[] = [];
    if (this.regrasPlano().length > 0) parts.push(`${this.regrasPlano().length} plano(s)`);
    if (this.regrasEmpresa().length > 0) parts.push(`${this.regrasEmpresa().length} empresa(s)`);
    if (this.regrasEstab().length > 0) parts.push(`${this.regrasEstab().length} estabelecimento(s)`);
    return parts.length > 0 ? parts.join(', ') : 'Nenhum (qualquer plano, empresa, profissional)';
  }

  comboRegraLabel(): string {
    const parts: string[] = [];
    if (this.combos().length > 0) parts.push(`${this.combos().length} combo(s)`);
    if (this.dependencias().length > 0) parts.push(`${this.dependencias().length} dependencia(s)`);
    return parts.length > 0 ? parts.join(', ') : 'Nenhum vinculo configurado';
  }

  tempoRelativo(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const diff = Date.now() - d.getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'agora';
    if (min < 60) return `ha ${min} min`;
    const hrs = Math.floor(min / 60);
    if (hrs < 24) return `ha ${hrs}h`;
    const dias = Math.floor(hrs / 24);
    return `ha ${dias}d`;
  }

  abreviarNome(nome: string): string {
    const parts = nome.trim().split(/\s+/);
    if (parts.length <= 1) return nome;
    return `${parts[0]} ${parts[parts.length - 1][0]}.`;
  }

  fmtR(v: number): string { return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }); }
  voltar(): void { this.router.navigateByUrl('/cupons'); }
  private limparMsg(): void { this.erro.set(null); this.sucesso.set(null); }

  protected fmt(iso: string): string {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso : d.toLocaleDateString('pt-BR');
  }
}
